import React from 'react';
import {Accordion, Box, TextFieldForm} from 'web-library';
import { TFunction, useTranslation } from 'react-i18next';
import {UseFormReturn} from 'react-hook-form';
import { CandidateProfileFormDefaultValues } from '../../../../models';
import ToggleDisplayEditFormItem from '../../../../components/ToggleDisplayEditFormItem';
import { useStyles } from './style';
import { disabledInput } from '../../logic/useUpdateRegistrationParticulars';
import ResidentialAddress from '../../../profile/components/CandidateProfileForm/HelperComponents/ResidentialAddress';
import { ContactDetailsItem } from '../../../../api/candidate/exam/model';
import { LookupListApiItem } from '../../../../api/user/model';
import MobileTelephoneContactDetails from './MobileTelephoneContactDetails';
import ForeignAddress from '../../../profile/components/CandidateProfileForm/HelperComponents/ForeignAddress';
import ControlledAutocomplete from '../../../../components/form-items/ControlledAutocomplete';
import SPERSDetails from './SPERSDetails';
import { useGetIntendedResidentialArea } from '../../../../api-refactored/non-national-exam-registration/useGetIntendedResidentialArea';
import { useGetIntendedResidentialZone } from '../../../../api-refactored/non-national-exam-registration/useGetIntendedResidentialZone';
import {
  ControllerRadioGroupWrapper
} from "../../../profile/components/CandidateProfileForm/HelperComponents/ControllerRadioGroupWrapper";

const useEmail = (contactDetails: ContactDetailsItem | undefined) => contactDetails?.email || '';

/**
 * To return mailing address info from the candidate details when formDefaultValues is not defined
 * in the screen that dont have edit mode.
 *
 * @param contactDetails candidate contact details
 */
const getMailingAddress = (
  contactDetails: ContactDetailsItem | undefined
): CandidateProfileFormDefaultValues | undefined =>
  contactDetails && {
    houseOrBlkNo: contactDetails.mailingAddress?.houseOrBlockNumber,
    streetName: contactDetails.mailingAddress?.streetName,
    building: contactDetails.mailingAddress?.buildingName,
    floor: contactDetails.mailingAddress?.floorNumber,
    unit: contactDetails.mailingAddress?.unitNumber,
    postalCode: contactDetails.mailingAddress?.postalCode,
    preferredAddress: contactDetails.localAddressPreferred ? 'L' : 'F',
  };

/**
 * To return foreign address info from the candidate details when formDefaultValues is not defined
 * in the screen that dont have edit mode.
 *
 * @param contactDetails candidate contact details
 */

const getForeignAddress = (
  contactDetails: ContactDetailsItem | undefined
): CandidateProfileFormDefaultValues | undefined =>
  contactDetails && {
    foreignAddress1: contactDetails.foreignAddress1,
    foreignAddress2: contactDetails.foreignAddress2,
    foreignAddress3: contactDetails.foreignAddress3,
    preferredAddress: contactDetails.localAddressPreferred ? 'L' : 'F',
  };

export interface ContactDetailsProps {
  contactDetails: ContactDetailsItem | undefined;
  form?: UseFormReturn<Record<string, any>>;
  isEditState?: boolean;
  formDefaultValues?: CandidateProfileFormDefaultValues;
  sameRowPhone?: boolean;
  sameRowAddress?: boolean;
  isAEIS?: boolean;
  isSPERS?: boolean;
  defaultExpanded?: boolean;
}

const isNonNationalExam = (isAEIS: boolean, isSPERS: boolean) => isAEIS || isSPERS;

const intendedResidentialName = (isAEIS: boolean) =>
  isAEIS ? 'intendedResidentialArea' : 'intendedResidentialZone';

const intendedResidentialLabel = (isAEIS: boolean, t: TFunction<'translation'>) =>
  isAEIS
    ? t('components->>ContactDetails.IntendedResidentialArea')
    : t('components->>ContactDetails.IntendedResidentialZone');

const intendedResidentialSubLabel = (isAEIS: boolean, t: TFunction<'translation'>) =>
  isAEIS && t('components->>ContactDetails.IntendedResidentialAreaHelperText');

const intendedResidentialOptions = (
  isAEIS: boolean,
  intendedResidentialArea: LookupListApiItem[] | undefined,
  intendedResidentialZone: LookupListApiItem[] | undefined
) => (isAEIS && intendedResidentialArea ? intendedResidentialArea : intendedResidentialZone || []);

const selectIntendedResidentialPlaceholder = (isAEIS: boolean) =>
  isAEIS ? 'Select intended residential area' : 'Select intended residential zone';

const ContactDetails = ({
  contactDetails,
  form,
  isEditState = false,
  formDefaultValues,
  sameRowPhone = false,
  sameRowAddress = false,
  isAEIS = false,
  isSPERS = false,
  defaultExpanded = true,
}: ContactDetailsProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const emailVal = useEmail(contactDetails);

  const { data: intendedResidentialArea } = useGetIntendedResidentialArea({
    enabled: isAEIS && isEditState,
  });

  const { data: intendedResidentialZone } = useGetIntendedResidentialZone({
    enabled: isSPERS && isEditState,
  });

  const getIntendedResidentialDisplay = () =>
    isAEIS ? contactDetails?.intendedResidentialArea : contactDetails?.intendedResidentialZone;

  return (
    <Accordion defaultExpanded={defaultExpanded} header={t('exam->>ReviewExam.ContactInformation')}>
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        containerClassName={classes.formElementContainer}
        labelText={t('components->>ContactDetails.EmailAddress')}
        id="inputText-candidateProfile-email"
        valueText={emailVal}
        EditComponent={
          <TextFieldForm
            form={form}
            id="inputText-candidateProfile-email"
            label={t('components->>ContactDetails.EmailAddress')}
            subLabel={isAEIS || isSPERS ? t('exam->>ReviewExam.EmailStatement') : ''}
            isSubLabelBelowLabel
            defaultValue={formDefaultValues?.email}
            name="email"
            type="text"
            error={!!form?.formState.errors.email}
            helperText={form?.formState.errors?.email?.message?.toString()}
            fullWidth
            inputProps={{ readOnly: disabledInput?.email }}
            isRequired
          />
        }
      />
      <MobileTelephoneContactDetails
        contactDetails={contactDetails}
        form={form}
        isEditState={isEditState}
        formDefaultValues={formDefaultValues}
        sameRowPhone={sameRowPhone}
      />
      <ControllerRadioGroupWrapper
          name="preferredAddress"
          control={form?.control}
          defaultValue={formDefaultValues?.preferredAddress ?? 'M'}
          row={sameRowPhone}>
        <Box className={`${sameRowAddress && classes.halfWidthLeftItem}`}>
          <ResidentialAddress
              isEditState={isEditState}
              formHook={form}
              isDisableInputs={disabledInput}
              formDefaultValues={formDefaultValues ?? getMailingAddress(contactDetails)}
              formDefaultValuesViewDetails={formDefaultValues ?? getMailingAddress(contactDetails)}
              isHideExtraFormInfo
              label={
                isEditState
                    ? t('components->>ContactDetails.MailingAddress')
                    : t('components->>ContactDetails.Residential Address')
              }
              formControlLabel={t('components->>ContactDetails.SetPreferredAddress')}
              isRequired
              isNonNational={isNonNationalExam(isAEIS, isSPERS)}
              sameRowAddress={sameRowAddress}
          />

          {isSPERS && (
              <ToggleDisplayEditFormItem
                  isEditState={isEditState}
                  labelText={t('components->>ContactDetails.IntendedResidentialZone')}
                  valueText={getIntendedResidentialDisplay()}
                  containerClassName={classes.formElementContainer}
                  EditComponent={
                      form && (
                          <ControlledAutocomplete<LookupListApiItem, false, false, undefined>
                              control={form.control}
                              name="intendedResidentialZone"
                              label={intendedResidentialLabel(isAEIS, t)}
                              subLabel={intendedResidentialSubLabel(isAEIS, t)}
                              isSubLabelBelowLabel={isAEIS}
                              placeholder={selectIntendedResidentialPlaceholder(isAEIS)}
                              id="input-select-intendedResidential"
                              error={!!form?.formState.errors.intendedResidentialZone}
                              helperText={form?.formState.errors?.intendedResidentialZone?.message?.toString()}
                              options={intendedResidentialOptions(
                                  isAEIS,
                                  intendedResidentialArea,
                                  intendedResidentialZone
                              )}
                              getOptionLabel={({ description }) => description}
                          />
                      )
                  }
              />
          )}
        </Box>

        <ForeignAddress
            isEditState={isEditState}
            formHook={form}
            isDisableInputs={disabledInput}
            formDefaultValues={formDefaultValues ?? getForeignAddress(contactDetails)}
            formDefaultValuesViewDetails={formDefaultValues ?? getForeignAddress(contactDetails)}
            isHideExtraFormInfo
            formControlLabel={t('components->>ContactDetails.SetPreferredAddress')}
            sameRowAddress={sameRowAddress}
        />
      </ControllerRadioGroupWrapper>
      {isAEIS && (
        <ToggleDisplayEditFormItem
          isEditState={isEditState}
          labelText={t('components->>ContactDetails.IntendedResidentialArea')}
          valueText={getIntendedResidentialDisplay()}
          containerClassName={classes.formElementContainer}
          EditComponent={
            form && (
              <ControlledAutocomplete<LookupListApiItem, false, false, undefined>
                control={form.control}
                name={intendedResidentialName(isAEIS)}
                label={intendedResidentialLabel(isAEIS, t)}
                subLabel={intendedResidentialSubLabel(isAEIS, t)}
                isSubLabelBelowLabel={isAEIS}
                placeholder={selectIntendedResidentialPlaceholder(isAEIS)}
                id="input-select-intendedResidential"
                error={
                  isAEIS
                    ? !!form?.formState.errors.intendedResidentialArea
                    : !!form?.formState.errors.intendedResidentialZone
                }
                helperText={
                  isAEIS
                    ? form?.formState.errors?.intendedResidentialArea?.message?.toString()
                    : form?.formState.errors?.intendedResidentialZone?.message?.toString()
                }
                options={intendedResidentialOptions(
                  isAEIS,
                  intendedResidentialArea,
                  intendedResidentialZone
                )}
                getOptionLabel={({ description }) => description}
                isRequired
              />
            )
          }
        />
      )}
      {isSPERS && (
        <SPERSDetails
          isEditState={isEditState}
          contactDetails={contactDetails}
          form={form}
          formDefaultValues={formDefaultValues}
        />
      )}
    </Accordion>
  );
};

export default ContactDetails;
